import { useEffect, useState } from 'react';
import { LineChart } from '@/components/charts/LineChart';
import { LiveValue, PageHeader, Stat, Switch } from '@/components/ui';
import { EntityLink } from '@/components/layout/workspace/EntityLink';
import { trends } from '@/pages/design/showcase/fixtures';
import { at } from '../../model';
import { Body, Page, Panel } from '../common';
import { NODES, diskWithForecast, useDrift } from './data';

/*
  Обзор приложения-образца. Живые цифры показывают свою свежесть (R25: кольцо до следующего
  опроса, остановка потока — выцветание и возраст), метрики несут историю подложкой плитки,
  линия диска продолжается в будущее пунктиром (R30), имена узлов живые (R26).
*/

const PERIOD = 4000;

export function InfraOverview() {
  const [live, setLive] = useState(true);
  const [age, setAge] = useState(0);
  const cpu = useDrift(34, 6, live, PERIOD);
  const la = useDrift(1.4, 0.5, live, PERIOD);
  const net = useDrift(1.2, 0.4, live, PERIOD);
  const [disk] = useState(() => diskWithForecast(Date.now()));

  useEffect(() => {
    if (live) return;
    const id = window.setInterval(() => setAge((a) => a + 1), 1000);
    return () => window.clearInterval(id);
  }, [live]);
  const ageLabel = age < 60 ? `${age} с назад` : `${Math.floor(age / 60)} мин назад`;

  return (
    <Page>
      <PageHeader
        title="Обзор"
        meta={
          <>
            <span>71 узел</span>
            <span>12 локаций</span>
            <span>9 842 VPS</span>
          </>
        }
        actions={
          <Switch
            size="sm"
            checked={live}
            onCheckedChange={(v) => {
              setLive(v);
              if (v) setAge(0);
            }}
            label="Поток метрик"
          />
        }
      />
      <Body>
        <Panel
          title="Сейчас по парку"
          aside={<span className="text-2xs text-fg-subtle">опрос раз в 4 с</span>}
        >
          <div className="flex flex-wrap gap-x-12 gap-y-4">
            <div>
              <div className="text-xs font-medium text-fg-muted">Процессор, средний</div>
              <LiveValue
                value={cpu.toFixed(1)}
                unit="%"
                live={live}
                intervalMs={PERIOD}
                ageLabel={ageLabel}
              />
            </div>
            <div>
              <div className="text-xs font-medium text-fg-muted">Load average</div>
              <LiveValue value={la.toFixed(2)} live={live} intervalMs={PERIOD} ageLabel={ageLabel} />
            </div>
            <div>
              <div className="text-xs font-medium text-fg-muted">Сеть</div>
              <LiveValue
                value={net.toFixed(2)}
                unit="Гбит/с"
                live={live}
                intervalMs={PERIOD}
                ageLabel={ageLabel}
              />
            </div>
          </div>
        </Panel>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Stat
            label="Процессор"
            value="34"
            unit="%"
            trend={trends.cpu}
            trendFill="background"
            trendTone="brand"
            delta={{ value: 6 }}
          />
          <Stat
            label="Память"
            value="61"
            unit="%"
            trend={trends.mem}
            trendFill="background"
            delta={{ value: 1, label: 'ровно' }}
          />
          <Stat
            label="Проблемы"
            value="12"
            trend={[6, 7, 7, 8, 9, 9, 10, 10, 11, 12, 12, 12]}
            trendFill="background"
            trendTone="danger"
            delta={{ value: 3, goodDirection: 'down', label: 'за сутки' }}
          />
          <Stat
            label="Диск /vds, худший узел"
            value="77"
            unit="%"
            trend={[71, 71, 72, 72, 73, 73, 74, 74, 75, 76, 76, 77]}
            trendFill="background"
            delta={{ value: 6, goodDirection: 'down', label: 'растёт третий день' }}
          />
        </div>

        <div className="grid gap-4 xl:grid-cols-3">
          <Panel title="Диск /vds на host-338" className="xl:col-span-2">
            <LineChart
              series={[{ id: 'disk', label: 'Диск', points: disk.points }]}
              forecast={{ points: disk.forecast, label: '90 % примерно через 11 дней' }}
              threshold={{ value: 90 }}
              unit="%"
              yMax={100}
              height={220}
              ariaLabel="Заполнение диска за 30 дней и прогноз на 12 дней вперёд"
            />
          </Panel>
          <Panel title="Беспокойные узлы">
            <ul className="flex flex-col divide-y divide-line">
              {NODES.filter((n) => n.incidents > 0).map((n) => (
                <li key={n.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <EntityLink
                    to={at(`/apps/infra/node/${n.id}`)}
                    kind="host"
                    name={n.id}
                    subtitle={`Гипервизор · ${n.role}`}
                    state={{ label: n.state, variant: n.state === 'Проблема' ? 'urgent' : 'success' }}
                    metrics={[
                      { label: 'Процессор', value: `${n.cpu} %`, trend: n.cpuTrend },
                      { label: 'Память', value: `${n.mem} %` },
                    ]}
                    note={`${n.incidents} за сутки`}
                  />
                  <span className="text-xs text-fg-subtle tabular-nums">{n.incidents} за сутки</span>
                </li>
              ))}
            </ul>
          </Panel>
        </div>
      </Body>
    </Page>
  );
}
